/* Design — the StudioCore poster editor (forked: features/design/editor, see FORK.md) inside the Brightivity
 * frame. Fork of studioCloud/src/pages/DesignEditorPage.jsx. A design opens from My library through
 * window.pendingDesignLoad (same contract as StudioCore). */
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrintStore } from '../features/print/PrintStore.jsx';
import i18n from '../i18n/index.js';
import StudioCoreEditor from '../features/design/editor/StudioCoreEditor.jsx';
import { Button, EmptyState } from '../ui/index.jsx';
import '../features/design/design.css';

function getInitialDesign() {
  if (window.pendingDesignLoad) {
    const d = window.pendingDesignLoad;
    window.pendingDesignLoad = null;
    return { designId: d.designId, json: d.json, name: d.name, imageUrl: d.imageUrl || null };
  }
  return null;
}

/** One fabric throw must not blank the page; the editor autosaves a draft, so a remount offers it back. */
class EditorErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, resetCount: 0 }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('Design editor crashed:', error, info && info.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bv-design__crash">
          <EmptyState icon="design" title={i18n.t('editor:page.crash.title')}
            action={<Button variant="blue" onClick={() => this.setState((s) => ({ hasError: false, resetCount: s.resetCount + 1 }))}>{i18n.t('editor:page.crash.reopen')}</Button>}>
            {i18n.t('editor:page.crash.subtitle')}
          </EmptyState>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetCount}>{this.props.children}</React.Fragment>;
  }
}

/** Leave the editor through its "Save your poster?" guard when the design has unsaved changes. */
export function guardedGo(go) {
  const g = window.__pseUnsavedGuard;
  if (g && g.isDirty()) g.request(go);
  else go();
}

export default function DesignPage() {
  const initialDesign = useMemo(() => getInitialDesign(), []);
  const navigate = useNavigate();
  const print = usePrintStore();
  useEffect(() => {
    // The editor's Print hands over a PNG Blob (StudioCore: window.pendingBrowserDownload + navigate-to-print);
    // here it becomes a Print Queue card.
    const toPrint = (e) => {
      const d = (e.detail && e.detail.downloadData) || window.pendingBrowserDownload;
      window.pendingBrowserDownload = null;
      if (!d || !d.blob) return;
      const name = `${String(d.name || 'Poster').replace(/\.(png|jpe?g)$/i, '')}.png`;
      guardedGo(() => { print.addFiles([new File([d.blob], name, { type: d.blob.type || 'image/png' })]); navigate('/print'); });
    };
    const go = (e) => { const to = e.detail && e.detail.to; if (to) guardedGo(() => navigate(to)); };
    const account = () => guardedGo(() => navigate('/settings'));
    window.addEventListener('navigate-to-print', toPrint);
    window.addEventListener('bv-navigate', go);
    window.addEventListener('pse-open-account', account);
    return () => {
      window.removeEventListener('navigate-to-print', toPrint);
      window.removeEventListener('bv-navigate', go);
      window.removeEventListener('pse-open-account', account);
    };
  }, [navigate, print]);
  return (
    <div className="bv-design" data-testid="design-page">
      <EditorErrorBoundary>
        <StudioCoreEditor initialDesign={initialDesign} />
      </EditorErrorBoundary>
    </div>
  );
}
